import {
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostListener,
	Inject,
	Injector,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AlreadyAddedValuesPipe } from 'lu-already-added-values-pipe';
import { LuIconType } from 'lu-icon';
import { SearchPipe } from 'lu-search-pipe';
import {
	DataService,
	DsConfig,
	KEY_BACKSPACE,
	KEY_DOWN_ARROW,
	KEY_ENTER,
	KEY_ESCAPE,
	KEY_UP_ARROW,
	ScrollService
} from 'lu-services';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, pairwise, tap } from 'rxjs/operators';
import {
	dataService as DataServiceToken,
	LuTypeaheadInstance,
	LuTypeaheadOptions,
	LuTypeaheadUrlOptions,
	scrollService as ScrollServiceToken
} from './lu-typeahead.models';
import { LuTypeaheadService } from './lu-typeahead.service';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'lu-typeahead',
	templateUrl: './lu-typeahead.component.html',
	styleUrls: ['./lu-typeahead.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LuTypeaheadComponent),
			multi: true
		},
		SearchPipe,
		AlreadyAddedValuesPipe
	]
})
export class LuTypeaheadComponent implements OnInit, OnDestroy, OnChanges {
	@Input() options: LuTypeaheadOptions;
	@Input() urlOptions: LuTypeaheadUrlOptions;
	@Input() parentForm: FormGroupDirective;
	@ViewChild('listContainer') listContainer: ElementRef<HTMLDivElement>;
	@ViewChild('input') input: ElementRef<HTMLInputElement>;
	@ViewChild('list') list: ElementRef<HTMLUListElement>;

	@Output() typeaheadReady: EventEmitter<LuTypeaheadInstance> = new EventEmitter<LuTypeaheadInstance>();

	/**
	 * Outputs internally selected values (full object, not just property set with selectAs option )
	 */
	@Output() internalSelectionChange: EventEmitter<any[]> = new EventEmitter<any[]>();

	/**
	 * Emits boolean whene'er dropdown opened state is changed
	 */
	@Output() dropdownStateChange = new EventEmitter<boolean>();

	// Offline mode items
	@Input() set initialItems(ar: any[]) {
		this.items$.next(ar);
		// Copy of all items so we can do local search and place results in this.items array
		this.originalItems = ar;
	}

	/**
	 * Used for setting internal typeahead state (internal selected values)
	 * So that we can add data to dynamic typeaheads with set selectAs property
	 * We inject whole objects, compare to model data approach that will inject selectAs property and wont display label
	 * Needed for: Sticky filters, Control bar view switching...
	 */
	@Input() set internalSelection(value: any[]) {
		if (value?.length) {
			this.internalSelectedValues = value;
			this.internalSelectionChange.next(value);
		}
	}

	/**
	 * List of items displayed in dropdown *(display filtered data)
	 */
	private items$ = new BehaviorSubject<any[]>(null);
	// eslint-disable-next-line rxjs/finnish
	public get items(): Observable<any[]> {
		return this.items$.asObservable();
	}
	/**
	 * Observable for tracking loading state
	 */
	private loadingData$ = new BehaviorSubject<boolean>(false);
	// eslint-disable-next-line rxjs/finnish
	public get loadingData(): Observable<boolean> {
		return this.loadingData$.asObservable();
	}
	/**
	 * List of items displayed in dropdown *(display filtered data)
	 */
	searchPlaceholder = '';
	/**
	 * Selected values (value only) eg. [1, 2, 3]
	 */
	selectedValues: any[] = [];
	/**
	 * Selected values (whole object) eg [{id: 1, name: 'John'}, {id: 2, name: 'Doe'}]
	 */
	internalSelectedValues: any[] = [];
	/**
	 * Offline original items used to search through them
	 */
	originalItems = [];
	isFocused = false;
	initFocus = false;
	dropdownOpened = false;
	alwaysOpen = false;
	isAbsolute = false;
	isScrollable = false; // flag for fixed height & scrollable option
	refreshAlreadyAddedValuesFilter = true;
	/**
	 * Activates multiselect view (dropdown with checkboxes)
	 */
	isMultiselectView = false;
	/**
	 * Shows footer with actions to reset, clear and applay selection (multiselect view)
	 */
	showFooterActions = false;
	/**
	 * Shows X for clearing selection on top filter input (multiselect view)
	 */
	showFilterClear = false;
	/**
	 * Keeps selected values on dropdown show
	 * used to compare with curent state to show adequate footer actions
	 */
	initialSelectionState: any[] = [];

	inputChange$ = new BehaviorSubject<object>({ value: '', key: '' });
	httpSubscription: Subscription = new Subscription();
	selectAs: string; // Only used for offline mode, because of the search pipe
	selectedAll: boolean;
	liveAnnouncer: string;
	chipOptionsField: string;
	firstLoad: boolean = true;
	private inputChangeSub: Subscription;
	private ngControl: NgControl;
	// eslint-disable-next-line rxjs/suffix-subjects
	private changeValue = new Subject<string>();
	private valueChangeSub: Subscription;
	// eslint-disable-next-line rxjs/suffix-subjects
	private initialDataLoadObs = new Subject<string>();
	private initialDataLoadSub: Subscription;
	private params: any = {};
	private page: number;
	private searchValue = '';
	private filterBy: string;
	private hasNext = true;
	private cursor: number;
	private minimumItemsInContainer = 5; // If we have less than 5 elements in the list we need to load more
	private typeaheadService: LuTypeaheadService;
	private insightsService: any;
	private trackingString: string;
	private previousDropdownOpened: boolean = null; // Used to track opened value changes to avoid duplicate emits
	LuIconType = LuIconType;

	@HostListener('window:click', ['$event'])
	handleClick(event: MouseEvent): void {
		if (!this.elementRef.nativeElement.contains(event.target) && this.dropdownOpened) {
			this.closeDropdown();
		}
	}

	constructor(
		@Inject(DataServiceToken) private dataService: DataService,
		@Inject(ScrollServiceToken) private scrollService: ScrollService,
		@Inject(SearchPipe) private searchPipe: SearchPipe,
		@Inject(AlreadyAddedValuesPipe) private alreadyAddedValuesPipe: AlreadyAddedValuesPipe,
		@Inject(ElementRef) private elementRef: ElementRef,
		@Inject(TranslateService) private translate: TranslateService,
		@Inject(Injector) private inj: Injector
	) {
		this.typeaheadService = new LuTypeaheadService(this);
	}

	ngOnInit(): void {
		this.ngControl = this.inj.get<NgControl>(NgControl);
		this.populateMetaData();
		this.listenToInputChange();
		this.initialLoad();
		this.chipOptionsField = this.typeaheadService.getChipOptionsField();
		this.typeaheadReady.emit({ service: this.typeaheadService });

		// Setting injected selected values without data load
		// If internalSelectedValues are set, remap their values to selectedValues to enable alreadySelectedValues pipe sort
		// to remove those values from loaded data
		if (this.internalSelectedValues?.length)
			this.selectedValues = this.internalSelectedValues?.map(item => item && item[this.selectAs]);
	}

	ngOnDestroy(): void {
		if (this.valueChangeSub) {
			this.valueChangeSub.unsubscribe();
		}
		if (this.inputChangeSub) {
			this.inputChangeSub.unsubscribe();
		}
		if (this.initialDataLoadSub) {
			this.initialDataLoadSub.unsubscribe();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.urlOptions || !!changes.urlOptions.firstChange) {
			return;
		}
		this.loadUrlOptions();
	}

	get valueChanges$(): Observable<string> {
		return this.changeValue.asObservable();
	}

	// Placeholders for the callbacks which are later provided
	// by the Control Value Accessor
	onChange: any = () => {};
	onTouch: any = () => {};

	onInputChange(events): void {
		const currentEvent = events[1];
		if (currentEvent.key === KEY_ENTER || currentEvent.key === KEY_UP_ARROW || currentEvent.key === KEY_DOWN_ARROW) {
			return;
		}

		const previousValue = events[0].value;
		const isBackspacePressed = currentEvent.key === KEY_BACKSPACE;
		const hasSelectedValues = this.internalSelectedValues?.length > 0;
		const shouldRemoveSelectedItem = !this.options.hideChips && !this.showFooterActions;

		if (!previousValue && isBackspacePressed && hasSelectedValues && shouldRemoveSelectedItem) {
			this.removeSelectedItem(this.internalSelectedValues.slice(-1)[0]);
		}
		// check if input is an object or string or whatever, should be triggered only if correct type
		this.searchValue = currentEvent.value;
		this.changeValue.next(currentEvent.value);
	}

	scrollBottom(element: ElementRef) {
		const { nativeElement } = element;
		if (nativeElement.scrollHeight > 0) {
			nativeElement.scrollTop = nativeElement.scrollHeight;
		}
	}

	// Set touched on blur
	onBlur(): void {
		this.isFocused = false;
		if (!this.alwaysOpen) {
			this.dropdownOpened = false;
			this.emitDropdownState();
		}

		if (this.ngControl) {
			this.ngControl.control.markAsTouched();
		}
	}

	setCursor(): void {
		if (!!this.cursor) {
			return;
		}
		this.cursor = 0;
	}

	// From ControlValueAccessor interface
	// Pass data from parent component
	writeValue(values: any): void {
		if (values instanceof Array) {
			// if we use online typeahead and we need to patch values through model, we don't have data so check bellow won't work
			// this patches values on the fly without any data already fetched just by setting model
			if (
				!!this.urlOptions &&
				!this.selectedValues.length &&
				!this.internalSelectedValues.length &&
				!this.items$.getValue().length
			) {
				this.internalSelectedValues = values;
				this.internalSelectionChange.next(this.internalSelectedValues);

				this.selectedValues = values.map(item => item && item[this.selectAs]);
				return;
			}

			values.forEach(value => {
				let objToAdd = null;
				if (!!this.options.maxSelections && this.selectedValues.length >= this.options.maxSelections) {
					return;
				}

				if (!!this.selectAs) {
					if (this.internalSelectedValues.some(item => item && item[this.selectAs] === value)) {
						return;
					}
					objToAdd = this.items$.getValue().filter(item => item && item[this.selectAs] === value)[0];
				} else {
					if (this.selectedValues.some(item => JSON.stringify(item) === JSON.stringify(value))) {
						return;
					}
					objToAdd = this.items$.getValue().filter(item => item === value)[0];
				}

				this.selectedValues.push(value);
				this.internalSelectedValues.push(objToAdd);
				this.internalSelectionChange.next(this.internalSelectedValues);

				this.refreshAlreadyAddedValuesFilter = !this.refreshAlreadyAddedValuesFilter;
			});
			return;
		}
		this.selectedValues = [];
		this.internalSelectedValues = [];
		this.internalSelectionChange.next(this.internalSelectedValues);
	}

	// From ControlValueAccessor interface
	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	// From ControlValueAccessor interface
	registerOnTouched(fn: any): void {
		this.onTouch = fn;
	}

	handleOptionClicked(selectedOpt: any, event?: MouseEvent): void {
		if (!selectedOpt) {
			return;
		}

		if (!!event) {
			event.preventDefault();
			event.stopPropagation();
		}

		this.addItem(selectedOpt);
		this.refreshAlreadyAddedValuesFilter = !this.refreshAlreadyAddedValuesFilter;

		if (
			this.urlOptions &&
			this.urlOptions.apiUrl &&
			this.items$.getValue().length - this.selectedValues.length < this.minimumItemsInContainer &&
			!this.loadingData$.getValue()
		) {
			this.search(this.searchValue, true);
		}

		// Emit values if footer is not visible, if footer is visible it will emit on apply action
		if (!this.showFooterActions) {
			this.onChange([...this.selectedValues]);
		}
	}

	isChecked(item): boolean {
		return this.internalSelectedValues.includes(item);
	}

	private addItem(selectedOpt: any): void {
		let valueToAdd = null;
		const { maxSelections } = this.options;
		if (!!maxSelections && this.selectedValues.length >= maxSelections) {
			return;
		}

		if (!!this.selectAs) {
			valueToAdd = selectedOpt[this.selectAs];
			if (this.internalSelectedValues.some(item => item[this.selectAs] === valueToAdd)) {
				return;
			}
		} else {
			if (this.selectedValues.some(item => JSON.stringify(item) === JSON.stringify(selectedOpt))) {
				return;
			}
			valueToAdd = selectedOpt;
		}
		this.setAnnouncer(
			`${selectedOpt[this.typeaheadService.getChipOptionsField()]} ${this.translate.instant(
				'lup.typeahead.selected_item'
			)}`
		);

		this.selectedValues.push(valueToAdd);
		this.internalSelectedValues.push(selectedOpt);
		this.internalSelectionChange.next(this.internalSelectedValues);

		// scrolls the container so input is visible
		if (this.isScrollable && !this.isMultiselectView) {
			this.scrollBottom(this.list);
		}

		if (!this.isMultiselectView && this.input.nativeElement.value !== '') {
			this.input.nativeElement.value = '';
			this.searchValue = '';
			this.search(this.searchValue);
		}
	}

	removeSelectedItem(item: any): void {
		const { disabled, selectAll } = this.options;
		if (disabled) {
			return;
		}
		const index = this.internalSelectedValues.findIndex(value => JSON.stringify(value) === JSON.stringify(item));
		this.selectedValues.splice(index, 1);
		this.internalSelectedValues.splice(index, 1);
		this.refreshAlreadyAddedValuesFilter = !this.refreshAlreadyAddedValuesFilter;
		this.search();
		if (this.selectedAll) {
			this.selectedAll = false;
			if (selectAll && selectAll.singleChip) {
				this.clearSelection();
				this.ngControl.control.markAsTouched();
				return;
			}
		}
		this.ngControl.control.markAsTouched();
		this.setAnnouncer(
			`${this.translate.instant('lup.typeahead.remove_chip')} ${item[this.typeaheadService.getChipOptionsField()]}`
		);

		this.internalSelectionChange.next(this.internalSelectedValues);

		// Emit values if footer is not visible, if footer is visible it will emit on apply action
		if (!this.showFooterActions) {
			this.onChange([...this.selectedValues]);
		}
	}

	clearSelection(): void {
		this.internalSelectedValues = [];
		this.internalSelectionChange.next(this.internalSelectedValues);

		this.selectedValues = [];
		this.onChange([]);
	}

	isValid(): boolean {
		let valid = true;
		if (this.ngControl && (this.ngControl.touched || (this.parentForm && this.parentForm.submitted))) {
			valid = this.ngControl.valid;
		}
		return valid;
	}

	focusInput(input: HTMLInputElement): void {
		const { readonly, disabled } = this.options;
		if (readonly || disabled) {
			return;
		}
		input.focus();
	}

	loadDataOnFocus(): void {
		const { readonly, disabled } = this.options;
		if (readonly || disabled) {
			return;
		}
		this.isFocused = true;
		this.dropdownOpened = true;
		this.emitDropdownState();
		this.initialDataLoadObs.complete();
	}

	closeDropdown(): void {
		if (!this.alwaysOpen) {
			this.dropdownOpened = false;
			this.emitDropdownState();

			if (this.showFooterActions) {
				this.clearSearch();
				this.cancelSelection();
			}
		}
	}

	multiselectBlur(): void {
		if (this.ngControl) {
			this.ngControl.control.markAsTouched();
		}
	}

	// TODO: refactor
	multiselectLoadDataOnFocus(): void {
		// Toggle multiselect dropdown or load data
		if (this.dropdownOpened) {
			this.cancelTracking();
			this.dropdownOpened = false;
			this.emitDropdownState();
		} else {
			this.loadTypeahedTracking();
			this.loadDataOnFocus();

			// Sets initial selection state
			this.initialSelectionState = this.internalSelectedValues.map(a => Object.assign({}, a));
		}
	}

	applySelection(): void {
		// TODO: refactor
		this.applyButtonTracking();
		this.initialSelectionState = this.internalSelectedValues.map(a => Object.assign({}, a));

		this.closeDropdown();
		this.onChange([...this.selectedValues]);
	}

	cancelSelection(): void {
		this.cancelTracking();
		this.internalSelectedValues = this.initialSelectionState.map(selectionState => Object.assign({}, selectionState));
		this.internalSelectionChange.next(this.internalSelectedValues);

		this.selectedValues = this.internalSelectedValues.map(item => item && item[this.selectAs]);
		this.refreshAlreadyAddedValuesFilter = !this.refreshAlreadyAddedValuesFilter;
		this.dropdownOpened = false;
		this.emitDropdownState();
	}

	isSelectionUnchanged(): boolean {
		// TODO: Change to use flag that is set on selection/deselection
		return JSON.stringify(this.initialSelectionState) === JSON.stringify(this.internalSelectedValues);
	}

	clearSearch(): void {
		this.inputChange$.next({ value: null, key: null });
	}

	multiselectClearSelection(): void {
		this.clearSearch();
		this.selectedValues = [];
		this.internalSelectedValues = [];
		this.internalSelectionChange.next(this.internalSelectedValues);
	}

	multiselectClearInitialSelection(): void {
		this.initialSelectionState = [];
	}

	onScrollDown(): void {
		this.triggerSearch(this.searchValue, true);
	}

	onHover(i): void {
		this.cursor = i;
	}

	isActive(item: any, index: number): boolean {
		// if (this.selectedItem) {
		// 	return this.selectedItem === item || index === this.cursor;
		// }
		return index === this.cursor;
	}

	@HostListener('keydown', ['$event'])
	handleKeyupEvent(event: KeyboardEvent): void {
		switch (event.key) {
			case KEY_UP_ARROW:
				event.preventDefault();
				this.keyScrollUp();
				break;
			case KEY_DOWN_ARROW:
				event.preventDefault();
				this.keyScrollDown();
				break;
			case KEY_ESCAPE:
				this.closeDropdown();
				break;
			case KEY_ENTER:
				event.preventDefault();
				if (!this.dropdownOpened) {
					this.dropdownOpened = true;
					this.emitDropdownState();
					return;
				}

				const containerArray = this.alreadyAddedValuesPipe.transform(
					this.items$.getValue(),
					this.selectedValues,
					!this.refreshAlreadyAddedValuesFilter,
					this.selectAs
				);
				this.handleOptionClicked(containerArray[this.cursor]);

				// If last element in the list is selected, get cursor one above
				if (containerArray[this.cursor] === containerArray[containerArray.length - 1]) {
					this.keyScrollUp();
				}
				break;
		}
	}

	selectAll(event): void {
		if (event) {
			event.preventDefault();
		}
		const { selectAll } = this.options;
		const { value, label, singleChip } = selectAll;
		if (singleChip) {
			const selectAllObj = {};
			selectAllObj[this.selectAs] = !!value || value === 0 ? value : -1;
			selectAllObj[this.typeaheadService.getChipOptionsField()] = !!label ? label : '';
			this.internalSelectedValues = [selectAllObj];
			this.internalSelectionChange.next(this.internalSelectedValues);

			this.onChange([value]);
		} else {
			this.items$.getValue().forEach(item => {
				this.addItem(item);
			});
			this.refreshAlreadyAddedValuesFilter = !this.refreshAlreadyAddedValuesFilter;
			this.onChange([...this.selectedValues]);
		}
		this.selectedAll = true;
	}

	trackByFn = (index: number, item: any): any => {
		if (!item) {
			return;
		}

		const trackBy = !!this.selectAs ? item[this.selectAs] : item;
		return trackBy;
	};

	generateButtonId(buttonName: string): string {
		return this.searchPlaceholder + '_' + buttonName;
	}

	private correctCursorAccessibility(): void {
		this.cursor = this.cursor ? this.cursor : 0;

		if (this.cursor > this.items$.getValue().length) {
			this.cursor = this.items$.getValue().length - 1;
		}
	}

	private keyScrollDown(): void {
		if (!this.dropdownOpened || !this.listContainer) {
			return;
		}
		this.correctCursorAccessibility();

		const indexOfNextEl = this.cursor + 2; // List starts from 1, curosr from 0, so we have to increment twice here
		if (this.scrollService.keyScrollDown(this.listContainer, indexOfNextEl)) {
			this.cursor++;
		}
	}

	private keyScrollUp(): void {
		if (!this.dropdownOpened || !this.listContainer) {
			return;
		}
		this.correctCursorAccessibility();

		// List starts from 1, curosr from 0, so we don't need to decrement here
		if (this.scrollService.keyScrollUp(this.listContainer, this.cursor)) {
			this.cursor--;
		}
	}

	private triggerSearch(value: string, loadMore: boolean): void {
		if (!this.urlOptions) {
			this.items$.next(this.searchPipe.transform(this.originalItems, value, this.filterBy));
		} else {
			this.search(value, loadMore);
		}
	}

	private search(val?: string, loadMore?: boolean): void {
		if ((!!loadMore && !this.hasNext) || !this.urlOptions) {
			return;
		}
		if (this.httpSubscription) {
			this.httpSubscription.unsubscribe();
		}
		this.showLoading();
		this.setAnnouncer(this.translate.instant('lup.typeahead.loading'), 1000);
		if (!loadMore) {
			this.firstLoad = true;
			this.page = 1;
		}
		if (!!this.page) {
			this.params.page = this.page;
		}
		if (!this.options.customExclude) {
			if (!!this.selectAs) {
				this.params.exclude = this.internalSelectedValues.map(item => item[this.selectAs]).join(',');
			} else {
				this.params.exclude = this.selectedValues.join(',');
			}
		}
		this.params[this.filterBy] = val;
		const dsConfig: DsConfig = this.dataService.getDefaultConfig(this.urlOptions.apiUrl, this.params);
		dsConfig.primarySpinnerConfig.show = false;
		this.httpSubscription = this.dataService
			.getList<any>(dsConfig)
			.pipe(finalize(() => this.hideLoading()))
			.subscribe(result => {
				const { data: newItems, has_next } = result;
				this.hasNext = !!has_next;

				this.firstLoad = false;
				if (!!loadMore) {
					this.items$.next([...this.items$.getValue(), ...newItems]);
				} else {
					this.items$.next(newItems);

					if (!!this.cursor) {
						this.cursor = 0;
					}

					if (!!this.listContainer) {
						this.listContainer.nativeElement.scrollTop = 0;
					}
				}

				const resultsAnnouncerString = this.hasNext
					? 'lup.typeahead.results_more_than'
					: 'lup.typeahead.results_less_than';
				const announcerResultsLength = this.alreadyAddedValuesPipe.transform(
					this.items$.getValue(),
					this.selectedValues,
					this.refreshAlreadyAddedValuesFilter,
					this.selectAs
				);
				this.setAnnouncer(
					this.translate.instant(resultsAnnouncerString, { result_number: announcerResultsLength.length })
				);

				this.page++;
			});
	}

	private initialLoad(): void {
		this.listenToOnFocus();
		if (!!this.alwaysOpen) {
			this.loadDataOnFocus();
		}
	}

	private listenToOnFocus(): void {
		this.initialDataLoadSub = this.initialDataLoadObs.subscribe({
			complete: () => {
				this.dropdownOpened = true;
				this.emitDropdownState();
				if (!!this.urlOptions && !!this.urlOptions.apiUrl) {
					this.search();
				}
			}
		});
	}

	private listenToInputChange(): void {
		this.valueChangeSub = this.valueChanges$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				tap(val => {
					this.triggerSearch(val, false);
				})
			)
			.subscribe();

		// We need to track both previous and current value here, if previous value is empty
		// and we hit backspace we want to delete last selected element, if previous value is not empty, we delete letter
		// That's why we use observable with pairwise
		this.inputChangeSub = this.inputChange$
			.pipe(
				pairwise(),
				tap(([prev, curr]) => {
					this.onInputChange([prev, curr]);
				})
			)
			.subscribe();
	}

	private populateMetaData(): void {
		this.loadOptions();
		this.loadUrlOptions();
	}

	private loadOptions(): void {
		if (!this.options) {
			return;
		}
		const {
			paginate,
			isAbsolute,
			alwaysOpen,
			selectAs,
			initFocus,
			filterBy,
			searchPlaceholder,
			isScrollable,
			isMultiselectView,
			showFooterActions,
			showFilterClear,
			insightsService,
			trackingString
		} = this.options;

		if (!!paginate) {
			this.page = 1;
		}

		if (!!isAbsolute) {
			this.isAbsolute = true;
		}

		if (!!alwaysOpen) {
			this.alwaysOpen = true;
		}

		if (!!selectAs) {
			this.selectAs = selectAs;
		}

		if (!!initFocus) {
			this.focusTypeahead();
		}

		if (!!isScrollable) {
			this.isScrollable = true;
		}

		if (!!isMultiselectView) {
			this.isMultiselectView = isMultiselectView;
		}

		if (!!showFooterActions) {
			this.showFooterActions = showFooterActions;
		}

		if (!!showFilterClear) {
			this.showFilterClear = showFilterClear;
		}

		if (!!insightsService) {
			this.insightsService = insightsService;

			if (this.trackingString === null) {
				throw new Error('Insights service initialized, but not trackingString');
			}
			this.trackingString = trackingString;
		}

		this.filterBy = filterBy;
		this.searchPlaceholder = searchPlaceholder || '';
	}

	private focusTypeahead(): void {
		this.initFocus = true;
		if (!!this.input) {
			this.input.nativeElement.focus();
			this.cursor = 0;
		}
	}

	private loadUrlOptions(): void {
		if (!!this.urlOptions && !!this.urlOptions.params) {
			this.params = this.urlOptions.params;
		}
	}

	private hideLoading(): void {
		this.loadingData$.next(false);
	}

	private showLoading(): void {
		this.loadingData$.next(true);
	}

	/**
	 * Announce to voice over some specific actions
	 * @param value
	 * @param duration
	 * @private
	 */
	private setAnnouncer(value: string, duration: number = 2000): void {
		this.liveAnnouncer = value;
		setTimeout(() => {
			this.liveAnnouncer = '';
		}, duration);
	}

	private applyButtonTracking(): void {
		if (this.insightsService == null) {
			return;
		}

		this.insightsService.actionEvent({
			pageelement: this.trackingString + '-filter-apply',
			action_type: 'CLICK_BUTTON',
			action_subtype: 'apply-button',
			parameters: this.buildParametersForTracking()
		});
	}

	private cancelTracking(): void {
		if (this.insightsService == null) {
			return;
		}

		this.insightsService.actionEvent({
			pageelement: this.trackingString + '-filter-cancel',
			action_type: 'CLICK_BUTTON',
			action_subtype: 'cancel-button',
			parameters: this.buildParametersForTracking()
		});
	}

	private loadTypeahedTracking(): void {
		if (this.insightsService == null) {
			return;
		}

		this.insightsService.actionEvent({
			pageelement: this.trackingString + '-filter',
			action_type: 'CLICK_FILTER',
			action_subtype: 'search_filter',
			parameters: {}
		});
	}

	private buildParametersForTracking(): Object {
		const obj = {
			search_filter_applied: true,
			num_items_selected: this.selectedValues.length
		};

		obj[this.trackingString + '_list'] = this.selectedValues.map(id => `urn:luapp:${this.trackingString}:id:${id}`);

		return obj;
	}

	/**
	 * Emits the current state of the dropdown (opened or closed) if it has changed since the last check.
	 *
	 * - If the dropdown is opened, the emission is delayed using `setTimeout` to avoid race conditions
	 *   caused by the dropdown closing on `onBlur` events. This ensures that the correct state is emitted
	 *   even when multiple dropdowns are involved.
	 * - If the dropdown is closed, the state is emitted immediately.
	 *
	 * Note:
	 * - Race conditions can occur when a previously active dropdown emits `opened: false` after losing focus,
	 *   while a new dropdown emits `opened: true`. This can result in an incorrect final state of `opened: false`.
	 * - A centralized service (e.g., `TypeaheadService`) could be used to manage the active typeahead state globally,
	 *   but this is not currently implemented due to each typeahead component using a separate instance.
	 *
	 * This behavior should be revisited and improved in future iterations.
	 */
	private emitDropdownState(): void {
		if (this.previousDropdownOpened !== this.dropdownOpened) {
			this.dropdownOpened
				? setTimeout(() => {
						this.dropdownStateChange.emit(this.dropdownOpened);
					}, 0)
				: this.dropdownStateChange.emit(this.dropdownOpened);
		}
		this.previousDropdownOpened = this.dropdownOpened;
	}
}
